import { postBillingInvoiceGetQuickbooksConnectionUrl, postBillingInvoiceSaveQuickbooksTokenFromCode } from '@apis/Invoices';
import { getBillingInvoiceGetQuickbooksConnectionStatus } from '@apis/Invoices';
import { QbAuthService } from '@root/Services/Connections/QbAuthService';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { useCallback, useEffect } from 'react';
import { useDiMemo } from '@root/Services/DI';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';

interface AuthCallbackMessage {
    type: 'QB_AUTH_CALLBACK';
    code?: string;
    state?: string;
    error?: string;
}

interface QuickbooksConnectionUrl {
    authorizeUrl: string;
    state: string;
    verifier: string;
}

let pendingState: string | null = null;
let pendingVerifier: string | null = null;
let authWindow: Window | null = null;
let messageHandler: ((event: MessageEvent) => void) | null = null;

function authenticateQuickbooks(connectionUrl: QuickbooksConnectionUrl, qbAuthSvc: QbAuthService): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('QB auth popup can only run in a browser environment.'));
    }

    return new Promise<void>(async (resolve, reject) => {
        try {
            const { authorizeUrl, state, verifier } = connectionUrl;

            pendingState = state;
            pendingVerifier = verifier;

            if (messageHandler) {
                window.removeEventListener('message', messageHandler);
            }

            messageHandler = async (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;

                const message = event.data as AuthCallbackMessage;
                if (message.type !== 'QB_AUTH_CALLBACK') return;

                window.removeEventListener('message', messageHandler!);
                messageHandler = null;

                if (authWindow && !authWindow.closed) {
                    authWindow.close();
                }
                authWindow = null;

                if (message.error) {
                    pendingState = pendingVerifier = null;
                    reject(new Error(`QB authentication error: ${message.error}`));
                    return;
                }

                if (!message.code || !message.state) {
                    pendingState = pendingVerifier = null;
                    reject(new Error('Missing code or state in QB callback'));
                    return;
                }

                if (message.state !== pendingState) {
                    pendingState = pendingVerifier = null;
                    reject(new Error('State mismatch — possible CSRF attack'));
                    return;
                }

                try {
                    const capturedVerifier = pendingVerifier!;
                    pendingState = pendingVerifier = null;

                    await postBillingInvoiceSaveQuickbooksTokenFromCode({
                        code: message.code,
                        verifier: capturedVerifier,
                        state: message.state,
                    });

                    qbAuthSvc.setToken('authenticated');
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };

            window.addEventListener('message', messageHandler);

            const width = 600;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            authWindow = window.open(
                authorizeUrl,
                'Quickbooks Authentication',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
            );

            if (!authWindow) {
                window.removeEventListener('message', messageHandler);
                messageHandler = null;
                reject(new Error('Failed to open authentication window. Please allow popups.'));
                return;
            }

            const checkClosed = setInterval(() => {
                if (authWindow && authWindow.closed) {
                    clearInterval(checkClosed);
                    if (messageHandler) {
                        window.removeEventListener('message', messageHandler);
                        messageHandler = null;
                    }
                    pendingState = pendingVerifier = null;
                    reject(new Error('Authentication window was closed'));
                }
            }, 500);
        } catch (error) {
            if (messageHandler) {
                window.removeEventListener('message', messageHandler);
                messageHandler = null;
            }
            pendingState = pendingVerifier = null;
            reject(error);
        }
    });
}

export function useQuickbooksAuthenticator() {
    const qbAuthSvc = useDiMemo(QbAuthService);
    const companyCtx = useDiMemo(CompanyContextService);

    return useCallback(async () => {
        if (qbAuthSvc.isAuthenticated()) {
            return;
        }

        const status = await getBillingInvoiceGetQuickbooksConnectionStatus(companyCtx.getRequestOptions(true));
        if (status.Connected) {
            qbAuthSvc.setToken('authenticated');
            return;
        }

        const connectionUrl = (await postBillingInvoiceGetQuickbooksConnectionUrl(
            companyCtx.getRequestOptions(true)
        )) as unknown as QuickbooksConnectionUrl;
        return await authenticateQuickbooks(connectionUrl, qbAuthSvc);
    }, [companyCtx.parentCompany.Id]);
}

export function QuickbooksCallback() {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code') || undefined;
        const state = params.get('state') || undefined;
        const error = params.get('error') || undefined;

        if (window.opener) {
            window.opener.postMessage({ type: 'QB_AUTH_CALLBACK', code, state, error }, window.location.origin);
        }

        setTimeout(() => window.close(), 500);
    }, []);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                fontFamily: 'system-ui, sans-serif',
                background: '#f5f5f5',
            }}
        >
            <div style={{ textAlign: 'center', padding: '2rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>Quickbooks Connected</h2>
                <p style={{ color: '#666' }}>This window will close automatically...</p>
            </div>
        </div>
    );
}
endpoint('_qb-callback', QuickbooksCallback, 'Quickbooks Connection Successful');
